/**
 * Layout constants for ticket / badge PDF generation.
 *
 * The PDF is built in TWO passes that MUST stay geometrically in sync:
 *  1. Shared template — the info card (labels "Nom :", "Prix TTC :", "Lieu :",
 *     "Organisateur :") is rendered via jsPDF `doc.html()` from
 *     `CustomPdfAppearanceSectionInformationCard`
 *     (apps/backoffice/src/pages/organiser/events/components/CustomPdfAppearanceSection.tsx)
 *     with an EMPTY name (every caller passes `userName: null`).
 *  2. Per attendee — the real name and QR are stamped on top with pdf-lib
 *     `drawText` / `drawImage` at the offsets below.
 *
 * SCALE = 1 INVARIANT: `doc.html()` is called with `width` but no `windowWidth`,
 * so jsPDF uses `scale = (width && windowWidth) ? width / windowWidth : 1` = 1
 * (jspdf 3.0.3, html worker). Hence 1 source CSS px === 1 PDF pt, and every
 * offset below is directly a distance in pt from its section origin.
 *
 * `card.*` place the pass-1 template; `name.*` / `qrImage.*` / `qrId.*` place the
 * pass-2 overlays ON TOP of that template. The info card uses a DETERMINISTIC
 * layout (header + bottom block are `shrink-0`, the middle block is `flex-1`), so
 * the "Nom :" row sits at a CONSTANT position regardless of event / ticket name
 * length. A single calibrated offset is therefore valid for every ticket — before
 * this, flex-shrink made the row move with the content and no constant could be
 * correct for all cards (e.g. a short "Event 1" vs a wrapping "MUSI PARC 2026").
 *
 * ⚠️ DESYNC RISK: if the info card CSS changes (sub-card heights, `leading-*`,
 * `gap-*`, `fontSize`, or the shrink/flex split in CustomPdfAppearanceSection.tsx),
 * the pass-2 overlay offsets here drift off their labels/slots and must be
 * re-measured. This is the exact class of bug that grouping them here makes visible.
 */

/**
 * Images embedded in an appearance template (custom-section pictures, cropped
 * header and background) are downscaled and JPEG-encoded with these settings
 * (custom-section pictures with transparent pixels stay PNG) so a template
 * stays light: every title PDF built from it embeds it, whether opened by a
 * ticket or invitation access link or printed at the cash register. Titles
 * are never attached to an email (ADR 0001).
 */
export const PDF_TEMPLATE_IMAGE_MAX_SIDE = 800;

export const PDF_TEMPLATE_IMAGE_JPEG_QUALITY = 0.8;

/** Model "fourQuarters" — QR + info card stacked in the top-left quadrant. */
export const FOUR_QUARTERS_PDF_LAYOUT = {
  /** Pass-1 `doc.html()` placement of the info card. x is centred: `(width - card.width) / 2`. */
  card: { width: 175, htmlTargetWidth: 400, htmlOriginY: 280 },
  /** Pass-2 QR image. x is centred: `(width - qrImage.size) / 2`. */
  qrImage: { size: 140, offsetY: 89 },
  qrId: { offsetX: 100, offsetY: 84, fontSize: 10 },
  /** Pass-2 attendee name — aligned on the "Nom :" label of the template card (matches its 8px size). */
  name: { offsetX: 99, offsetY: 351, fontSize: 8 },
} as const;

/** Models "horizontal" and "mix" — QR + info card side by side in a banner row. */
export const LINE_PDF_LAYOUT = {
  /** Pass-1 `doc.html()` placement of the info card (fixed x offset). */
  card: { htmlTargetWidth: 200, htmlOriginX: 260, htmlOriginY: 140 },
  qrImage: { size: 100, offsetX: 80, offsetY: 133 },
  qrId: { offsetX: 80, offsetY: 125, fontSize: 10 },
  /**
   * Pass-2 attendee name — aligned on the "Nom :" label of the template card
   * (matches its 8px size).
   * TODO(ali): re-calibrated for the deterministic layout from analysis only;
   * verify on a real horizontal/mix ticket render and adjust if needed.
   */
  name: { offsetX: 297, offsetY: 202, fontSize: 8 },
} as const;
